import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { translations } from "@/lib/translations";
import { storyTranslations } from "@/lib/storyTranslations";

const LanguageContext = createContext(null);

const SUPPORTED = ["ar", "en", "tr", "zh-CN"];

function detectInitialLang() {
  try {
    // ?lang= wins: the sitemap's hreflang alternates point crawlers at these URLs.
    const fromUrl = new URLSearchParams(window.location.search).get("lang");
    if (fromUrl && SUPPORTED.includes(fromUrl)) return fromUrl;
    const stored = localStorage.getItem("misk_lang");
    if (stored && SUPPORTED.includes(stored)) return stored;
    const browser = (navigator.language || "en").toLowerCase();
    if (browser.startsWith("ar")) return "ar";
    if (browser.startsWith("tr")) return "tr";
    return "en";
  } catch {
    return "ar";
  }
}

export function LanguageProvider({ children }) {
  // Client-only app: detect synchronously so there is no flash of the wrong language.
  const [lang, setLang] = useState(detectInitialLang);

  useEffect(() => {
    const t = translations[lang] || translations.ar;
    document.documentElement.lang = t.locale;
    document.documentElement.dir = t.dir;
    try {
      localStorage.setItem("misk_lang", lang);
    } catch {}
  }, [lang]);

  const t = useMemo(() => {
    const base = translations[lang] || translations.ar;
    const story = storyTranslations[lang] || storyTranslations.ar;
    return {
      ...base,
      ...story,
      nav: { ...base.nav, ...story.nav },
      hero: { ...base.hero, ...story.hero },
    };
  }, [lang]);

  const changeLang = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLang(next);
  }, []);

  return (
    <LanguageContext.Provider value={{ lang, t, changeLang, supported: SUPPORTED }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
